import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function useCountdown(target) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!target) return;
    const upd = () => setLeft(Math.max(0, Math.floor((new Date(target).getTime() - Date.now()) / 1000)));
    upd();
    const t = setInterval(upd, 1000);
    return () => clearInterval(t);
  }, [target]);
  const d = Math.floor(left / 86400);
  const h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  return { d, h, m, s };
}

export default function HolderDashboard() {
  const [wallet, setWallet] = useState("");
  const [balance] = useState({ qdt: 0, circulating: 0 });
  const [payouts, setPayouts] = useState([]);
  const [eta, setEta] = useState(null);

  useEffect(() => {
    api.get("/holders/payouts").then((r) => setPayouts(r.data || [])).catch(() => {});
    api.get("/holders/next-eta").then((r) => setEta(r.data.next_distribution_at)).catch(() => {});
  }, []);

  const cd = useCountdown(eta);
  const sharePct = balance.circulating > 0 ? (balance.qdt / balance.circulating) * 100 : 0;

  const items = payouts;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-[#FFC940]">holder dashboard</div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black">$QUODUST <span className="ember-text">campfire</span></h1>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <section className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5 lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-stone-500">your Robinhood Chain wallet</div>
          <div className="mt-2 flex gap-2">
            <Input
              data-testid="holder-wallet-input"
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
              placeholder="paste wallet · we'll (mock) read $QUODUST balance"
              className="bg-[#0A0906] border-[#3A2E1C]"
            />
            <Button className="bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold" data-testid="holder-wallet-check">read</Button>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            <Card label="$QUODUST balance" value={balance.qdt.toLocaleString()} hint="chain id 4663" />
            <Card label="share of pool" value={`${sharePct.toFixed(3)}%`} hint="of circulating supply" />
            <Card label="lifetime yield" value={fmtUSD(items.reduce((a, p) => a + p.total_pool_usd * (sharePct / 100), 0))} hint="pro-rata earned" />
          </div>
        </section>

        <section className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
          <div className="text-xs uppercase tracking-widest text-stone-500">next distribution</div>
          <div className="mt-3 grid grid-cols-4 gap-2 text-center">
            {[["days", cd.d], ["hrs", cd.h], ["min", cd.m], ["sec", cd.s]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-[#3A2E1C] bg-[#0A0906] py-3">
                <div className="font-display text-2xl font-bold ember-text">{String(v).padStart(2, "0")}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-stone-500">Monthly cadence. Snapshot taken at month-end, yield distributed manually by the team until onchain streaming ships.</p>
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
        <div className="font-display text-lg mb-3">payout ledger</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-stone-500">
              <tr><th className="text-left py-2">period</th><th className="text-right">pool</th><th className="text-right">NVDA</th><th className="text-right">TSLA</th><th className="text-right">AAPL</th><th className="text-right">SPY</th><th className="text-right">your pro-rata</th></tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={i} className="border-t border-[#1E1810]" data-testid="holder-payout-row">
                  <td className="py-2 mono">{p.period_label}</td>
                  <td className="text-right mono text-[#FFD54F]">{fmtUSD(p.total_pool_usd)}</td>
                  <td className="text-right mono">{fmtUSD(p.stock_breakdown?.NVDA || 0)}</td>
                  <td className="text-right mono">{fmtUSD(p.stock_breakdown?.TSLA || 0)}</td>
                  <td className="text-right mono">{fmtUSD(p.stock_breakdown?.AAPL || 0)}</td>
                  <td className="text-right mono">{fmtUSD(p.stock_breakdown?.SPY || 0)}</td>
                  <td className="text-right mono ember-text">{fmtUSD(p.total_pool_usd * (sharePct / 100))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Card({ label, value, hint }) {
  return (
    <div className="rounded-md border border-[#3A2E1C] bg-[#0A0906] p-4">
      <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="mt-1.5 font-display text-xl font-bold">{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-stone-500">{hint}</div>}
    </div>
  );
}
